import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

const FIREBASE_URL =
  "https://portfolio-react-92097-default-rtdb.europe-west1.firebasedatabase.app/.json";

const PLACEHOLDER_IMAGE = "/images/project-placeholder.jpg";

const normalizeProjects = (data) => {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data
      .filter(Boolean)
      .map((project, index) => ({
        id: project.id || String(index),
        ...project,
      }));
  }

  if (data.projects) {
    if (Array.isArray(data.projects)) {
      return data.projects
        .filter(Boolean)
        .map((project, index) => ({
          id: project.id || String(index),
          ...project,
        }));
    }

    if (typeof data.projects === "object") {
      return Object.entries(data.projects)
        .filter(
          ([, project]) =>
            project && typeof project === "object"
        )
        .map(([id, project]) => ({
          id,
          ...project,
        }));
    }
  }

  if (typeof data === "object") {
    return Object.entries(data)
      .filter(
        ([, project]) =>
          project &&
          typeof project === "object" &&
          (project.title || project.slug)
      )
      .map(([id, project]) => ({
        id,
        ...project,
      }));
  }

  return [];
};

const DetailSection = ({ title, children }) => {
  if (!children) {
    return null;
  }

  return (
    <section className="border-t border-zinc-200 pt-9">
      <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">
        {title}
      </h2>

      <div className="mt-4 text-lg leading-8 text-zinc-600">
        {children}
      </div>
    </section>
  );
};

const ProjectDetails = () => {
  const { slug } = useParams();

  const [project, setProject] = useState(null);
  const [imageSource, setImageSource] = useState(
    PLACEHOLDER_IMAGE
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "instant",
    });
  }, [slug]);

  useEffect(() => {
    const fetchProject = async () => {
      setIsLoading(true);
      setError("");

      try {
        const response = await axios.get(FIREBASE_URL);
        const projects = normalizeProjects(response.data);

        const matchingProject = projects.find(
          (item) => item.slug === slug
        );

        if (!matchingProject) {
          setError("This project could not be found.");
          return;
        }

        setProject(matchingProject);
        setImageSource(
          matchingProject.images?.[0]?.path ||
            PLACEHOLDER_IMAGE
        );
      } catch (requestError) {
        console.error(
          "Failed to load project:",
          requestError
        );

        setError(
          "The project could not be loaded. Please try again."
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchProject();
  }, [slug]);

  if (isLoading) {
    return (
      <main className="min-h-screen bg-zinc-50 px-6 py-20 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <div className="h-8 w-40 animate-pulse rounded-lg bg-zinc-200" />
          <div className="mt-10 h-16 max-w-3xl animate-pulse rounded-xl bg-zinc-200" />
          <div className="mt-8 h-[480px] animate-pulse rounded-3xl bg-zinc-200" />
        </div>
      </main>
    );
  }

  if (error || !project) {
    return (
      <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-zinc-50 px-6">
        <div className="max-w-lg rounded-3xl border border-zinc-200 bg-white p-10 text-center shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-indigo-600">
            Project unavailable
          </p>

          <h1 className="mt-4 text-3xl font-semibold text-zinc-950">
            {error || "This project could not be found."}
          </h1>

          <Link
            to="/projects/home"
            className="mt-8 inline-flex rounded-xl bg-zinc-950 px-6 py-3 font-semibold text-white transition hover:bg-zinc-800"
          >
            Back to projects
          </Link>
        </div>
      </main>
    );
  }

  const tags = Array.isArray(project.tags)
    ? project.tags
    : [];

  const categories = Array.isArray(project.categories)
    ? project.categories
    : [];

  const highlights = Array.isArray(project.highlights)
    ? project.highlights
    : [];

  const hasWebsite =
    typeof project.website === "string" &&
    project.website.trim() !== "";

  const hasGitHub =
    typeof project.github === "string" &&
    project.github.trim() !== "";

  const statusClasses =
    project.status === "In development"
      ? "border-amber-200 bg-amber-50 text-amber-700"
      : "border-emerald-200 bg-emerald-50 text-emerald-700";

  return (
    <main className="min-h-screen bg-zinc-50 text-zinc-950">
      {/* Header */}
      <section className="border-b border-zinc-200 bg-white px-6 py-16 sm:px-10 lg:px-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-6xl"
        >
          <Link
            to="/projects/home"
            className="inline-flex items-center gap-2 text-sm font-semibold text-zinc-600 transition hover:text-indigo-600"
          >
            <span aria-hidden="true">←</span>
            Back to projects
          </Link>

          <div className="mt-10 flex flex-wrap items-center gap-3">
            <span
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                statusClasses
              }`}
            >
              {project.status || "Completed"}
            </span>

            {project.date && (
              <span className="text-sm font-medium text-zinc-500">
                {project.date}
              </span>
            )}

            {project.featured && (
              <span className="rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                Featured project
              </span>
            )}
          </div>

          <h1 className="mt-6 max-w-5xl text-4xl font-semibold tracking-tight text-zinc-950 sm:text-6xl">
            {project.title}
          </h1>

          <p className="mt-7 max-w-3xl text-xl leading-9 text-zinc-600">
            {project.shortDescription ||
              project.description}
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            {hasWebsite && (
              <a
                href={project.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-xl bg-zinc-950 px-6 py-3.5 font-semibold text-white transition hover:-translate-y-0.5 hover:bg-zinc-800"
              >
                View live demo
                <span aria-hidden="true">↗</span>
              </a>
            )}

            {hasGitHub && (
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-xl border border-zinc-300 bg-white px-6 py-3.5 font-semibold text-zinc-900 transition hover:-translate-y-0.5 hover:bg-zinc-50"
              >
                View source code
              </a>
            )}
          </div>
        </motion.div>
      </section>

      {/* Main content */}
      <section className="px-6 py-16 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              delay: 0.1,
              duration: 0.6,
            }}
            className="overflow-hidden rounded-3xl border border-zinc-200 bg-zinc-100 shadow-sm"
          >
            <img
              src={imageSource}
              alt={
                project.images?.[0]?.caption ||
                `${project.title} project visual`
              }
              onError={() => {
                if (imageSource !== PLACEHOLDER_IMAGE) {
                  setImageSource(PLACEHOLDER_IMAGE);
                }
              }}
              className="max-h-[650px] w-full object-cover"
            />
          </motion.div>

          {project.images?.[0]?.caption && (
            <p className="mt-3 text-center text-sm text-zinc-500">
              {project.images[0].caption}
            </p>
          )}

          <div className="mt-16 grid gap-12 lg:grid-cols-[1fr_320px]">
            {/* Written details */}
            <div className="space-y-10">
              <DetailSection title="Project overview">
                <p>{project.description}</p>
              </DetailSection>

              <DetailSection title="The problem">
                <p>{project.problem}</p>
              </DetailSection>

              <DetailSection title="The solution">
                <p>{project.solution}</p>
              </DetailSection>

              <DetailSection title="My role">
                <p>{project.role}</p>
              </DetailSection>

              {highlights.length > 0 && (
                <section className="border-t border-zinc-200 pt-9">
                  <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">
                    Key highlights
                  </h2>

                  <ul className="mt-6 space-y-4">
                    {highlights.map((highlight) => (
                      <li
                        key={highlight}
                        className="flex gap-4 text-lg leading-8 text-zinc-600"
                      >
                        <span className="mt-3 h-2 w-2 shrink-0 rounded-full bg-indigo-600" />
                        <span>{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Project metadata */}
            <aside className="h-fit rounded-3xl border border-zinc-200 bg-white p-7 shadow-sm lg:sticky lg:top-24">
              {tags.length > 0 && (
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-zinc-500">
                    Frameworks and languages
                  </h2>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-sm font-medium text-zinc-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {categories.length > 0 && (
                <div className="mt-8 border-t border-zinc-200 pt-7">
                  <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-zinc-500">
                    Project type
                  </h2>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {categories.map((category) => (
                      <span
                        key={category}
                        className="rounded-full bg-indigo-50 px-3 py-1.5 text-sm font-medium text-indigo-700"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-8 border-t border-zinc-200 pt-7">
                <p className="text-sm font-medium text-zinc-500">
                  Year
                </p>

                <p className="mt-1 font-semibold text-zinc-950">
                  {project.date || "Not specified"}
                </p>
              </div>

              <div className="mt-6">
                <p className="text-sm font-medium text-zinc-500">
                  Status
                </p>

                <p className="mt-1 font-semibold text-zinc-950">
                  {project.status || "Completed"}
                </p>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ProjectDetails;
